import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../../hooks";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  useStartPeeping,
  useEndPeeping,
  useGetFlashCardByID,
} from "../Agora/apis/NewKarokeApi";
import NewKaraokeCardLiveStreamContent from "../Agora/NewKaraokeCard";
import PaymentForKaraoke from "../Payment/PaymentforKaraoke";
import PaymentForKaraokeRequest from "../Payment/PaymentforKaraokeRequest";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Close, MusicNote, Book, Send, NearMe } from "@mui/icons-material";

import { enqueueSnackbar } from "notistack";
import { useNotificationStore } from "../../../../services/store/usenotificationstore";
import { SocketNotificationType } from "../../../../constants/appConstants";
import NewKaraokeCard from "../Agora/NewKaraokeCard";

const PAYMENT_TIMEOUT = 15000; // 15 seconds

const peepingErrorMessage = {
  NOT_PURCHASED: "Card has not been purchased",
  NOT_FOUND: "Card not found",
  NOT_STARTED: "Card has not been started",
};

export function KaraokeViewUser() {
   const { channel_name } = useParams();
  const { userInfo } = useAuth();
  const purchasedChips = userInfo?.wallet_purchase?.available_chips || 0;
  const earnedChips = userInfo?.wallet_earning?.available_chips || 0;
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const flashCardId = searchParams.get("flashCardId");

  // State
  const [isPaymentRequired, setIsPaymentRequired] = useState(false);
  const hasStartedPeeping = useRef(false);

  // Song Request / Story Text Dialog State
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestTab, setRequestTab] = useState(0); // 0 = Song Request, 1 = Story Text
  const [songRequest, setSongRequest] = useState("");
  const [storyText, setStoryText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { currentNotification, clearCurrentNotification } =
    useNotificationStore();

  const [sessionEnded, setSessionEnded] = useState(false);

  useEffect(() => {
    console.log(currentNotification, "currentNotification");
    if (
      currentNotification?.notification_type ===
        SocketNotificationType.FlashCardEnded ||
      currentNotification?.notification_type === "flash-card-ended"
    ) {
      enqueueSnackbar("The talent has ended the flash card session", {
        variant: "info",
      });
      setSessionEnded(true);
      clearCurrentNotification();
    }
  }, [clearCurrentNotification, currentNotification]);

  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<{
    type: "song" | "story";
    text: string;
  } | null>(null);

  // API Hooks
  const { mutate: startPeeping } = useStartPeeping({
    key: ["start-peeping"],
    params: { id: flashCardId! },
  });

  const { mutate: endPeeping } = useEndPeeping({
    key: ["end-peeping"],
    params: { id: channel_name! },
  });

  const { data } = useGetFlashCardByID({
    key: ["flash-cards", userInfo?.id],
    params: {
      id: Number(flashCardId),
    },
  });

  const cardData = data?.data;

  console.log(cardData, "cardData");
  // Handlers
  const handleStartPeeping = useCallback(() => {
    if (hasStartedPeeping.current) return;
    startPeeping(
      {},
      {
        onSuccess: () => {
          hasStartedPeeping.current = true;
        },
        onError: (error: any) => {
          if (
            error.response?.data?.message === peepingErrorMessage.NOT_PURCHASED
          ) {
            setIsPaymentRequired(true);
          }
        },
      }
    );
  }, [startPeeping]);

  const handleEndPeeping = useCallback(() => {
    if (hasStartedPeeping.current) {
      endPeeping({});
    }
  }, [endPeeping]);

  const handleCallEnd = () => {
    window.location.href = "/app-deck?filter=Flash_Card";
  };

  const handleOpenRequestDialog = () => {
    setShowRequestDialog(true);
    setRequestTab(0);
    setSongRequest("");
    setStoryText("");
    setSubmitSuccess(false);
    setSubmitError(null);
  };

  const handleCloseRequestDialog = () => {
    setShowRequestDialog(false);
    setRequestTab(0);
    setSongRequest("");
    setStoryText("");
    setSubmitSuccess(false);
    setSubmitError(null);
  };

  const handleSubmitRequest = async () => {
    const requestText =
      requestTab === 0 ? songRequest.trim() : storyText.trim();

    if (!requestText) {
      setSubmitError(
        requestTab === 0 ? "Please enter a song request" : "Please enter story text"
      );
      return;
    }

    // Store the pending request and show payment modal
    setPendingRequest({
      type: requestTab === 0 ? "song" : "story",
      text: requestText,
    });
    setShowRequestDialog(false);
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = async () => {
    if (!pendingRequest || !flashCardId) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // TODO: Replace with actual API endpoint when available
      // Example: await sendFlashCardRequest({
      //   flashCardId: Number(flashCardId),
      //   type: pendingRequest.type,
      //   text: pendingRequest.text
      // });

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSubmitSuccess(true);
      enqueueSnackbar(
        pendingRequest.type === "song"
          ? "Song request submitted successfully!"
          : "Story submitted successfully!",
        { variant: "success" }
      );

      // Reset states
      setPendingRequest(null);
      setSongRequest("");
      setStoryText("");
      setRequestTab(0);

      setTimeout(() => {
        setShowPaymentModal(false);
        setSubmitSuccess(false);
      }, 1500);
    } catch (error: any) {
      console.error("Failed to submit request:", error);
      setSubmitError(
        error?.response?.data?.message || "Failed to submit request"
      );
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to submit request",
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPendingRequest(null);
    // Reopen the request dialog if user cancels payment
    if (songRequest.trim() || storyText.trim()) {
      setShowRequestDialog(true);
    }
  };

  // Effects
  useEffect(() => {
    handleStartPeeping();

    const paymentTimer = setTimeout(() => {
      startPeeping(
        {},
        {
          onError: (error: any) => {
            setIsPaymentRequired((prev) => {
              if (prev) return prev;
              if (
                error.response?.data?.message ===
                peepingErrorMessage.NOT_PURCHASED
              ) {
                return true;
              }
              return prev;
            });
          },
        }
      );
      handleEndPeeping();
    }, PAYMENT_TIMEOUT);

    return () => {
      clearTimeout(paymentTimer);
    };
  }, [handleStartPeeping, handleEndPeeping, startPeeping]);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  // // Payment Modal
  if (isPaymentRequired) {
    return (
      <PaymentForKaraoke
        open={isPaymentRequired}
        onClose={() => setIsPaymentRequired(false)}
        streamId={channel_name!}
        flashCardId={Number(flashCardId)}
        title={cardData?.title || ""}
        peepPrice={cardData ? Number(cardData.price) : 0}
        purchaseChip={purchasedChips}
        earnedChip={earnedChips}
        cardId={cardData?.channel_name?.toString() || ""}
      />
    );
  }

  // Main Component
  return (
    <>
      <NewKaraokeCard
        displayName="Regular Session"
        onCallEnd={handleCallEnd}
        userId={userInfo?.id + ""}
         videoId={channel_name!}
        username={userInfo?.username}
        role="audience"
        flashCardId={flashCardId}
        onRequestClick={handleOpenRequestDialog}
        profileImage={
          cardData?.talent?.profile_image ??
          cardData?.business?.profile_image ??
          ""
        }
        sessionEnded={sessionEnded}
      />

      {/* Song Request / Story Text Dialog */}
      <Dialog
        open={showRequestDialog}
        onClose={handleCloseRequestDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: "#1A1A2E",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: { xs: 18, sm: 20 }, color: "#FFFFFF" }}
          >
            Send Request
          </Typography>
          <IconButton onClick={handleCloseRequestDialog} sx={{ color: "#FFFFFF" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: "rgba(255,255,255,0.12)" }}>
          <Tabs
            value={requestTab}
            onChange={(_, newValue) => setRequestTab(newValue)}
            sx={{
              px: 2,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                fontSize: { xs: 14, sm: 15 },
                minHeight: 48,
                color: "rgba(255,255,255,0.6)",
              },
              "& .MuiTab-root.Mui-selected": { color: "#FFFFFF" },
              "& .MuiTabs-indicator": { backgroundColor: "#E50914" },
            }}
          >
            <Tab
              icon={<MusicNote sx={{ fontSize: 18, mr: 1 }} />}
              iconPosition="start"
              label="Song Request"
            />
            <Tab
              icon={<Book sx={{ fontSize: 18, mr: 1 }} />}
              iconPosition="start"
              label="Story Text"
            />
          </Tabs>
        </Box>

        <DialogContent sx={{ pt: 3.5, px: 3.5, pb: 2.5 }}>
          {requestTab === 0 && (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  color: "rgba(255,255,255,0.6)",
                  mb: 3.5,
                  lineHeight: 1.7,
                  fontSize: { xs: 14, sm: 15 },
                }}
              >
                Request a song you'd like the talent to perform for you.
              </Typography>
              <TextField
                fullWidth
                label="Song Name"
                placeholder="Enter song name..."
                value={songRequest}
                onChange={(e) => setSongRequest(e.target.value)}
                disabled={isSubmitting || submitSuccess}
                multiline
                rows={4}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    fontSize: { xs: 14, sm: 15 },
                    color: "#FFFFFF",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.15)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.3)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E50914",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,0.5)",
                    "&.Mui-focused": { color: "#E50914" },
                  },
                }}
              />
            </Box>
          )}

          {requestTab === 1 && (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  color: "rgba(255,255,255,0.6)",
                  mb: 3.5,
                  lineHeight: 1.7,
                  fontSize: { xs: 14, sm: 15 },
                }}
              >
                Share a story or text you'd like the talent to read for you.
              </Typography>
              <TextField
                fullWidth
                label="Story Text"
                placeholder="Enter your story or text..."
                value={storyText}
                onChange={(e) => setStoryText(e.target.value)}
                disabled={isSubmitting || submitSuccess}
                multiline
                rows={6}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    fontSize: { xs: 14, sm: 15 },
                    color: "#FFFFFF",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.15)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.3)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E50914",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,0.5)",
                    "&.Mui-focused": { color: "#E50914" },
                  },
                }}
              />
            </Box>
          )}

          {submitSuccess && (
            <Alert
              severity="success"
              sx={{
                mb: 2,
                backgroundColor: "rgba(16, 185, 129, 0.15)",
                color: "#10B981",
                border: "1px solid rgba(16, 185, 129, 0.3)",
                borderRadius: 2,
              }}
            >
              Request submitted successfully!
            </Alert>
          )}

          {submitError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                backgroundColor: "rgba(229, 9, 20, 0.15)",
                color: "#E50914",
                border: "1px solid rgba(229, 9, 20, 0.3)",
                borderRadius: 2,
              }}
              onClose={() => setSubmitError(null)}
            >
              {submitError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3.5, pb: 3.5, pt: 2.5, gap: 2 }}>
          <Button
            onClick={handleCloseRequestDialog}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: { xs: 14, sm: 15 },
              borderRadius: 3,
              px: 3.5,
              py: 1.25,
              borderColor: "rgba(255,255,255,0.25)",
              color: "#FFFFFF",
              "&:hover": { borderColor: "rgba(255,255,255,0.5)", backgroundColor: "rgba(255,255,255,0.05)" },
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleSubmitRequest}
            variant="contained"
            disabled={
              isSubmitting ||
              submitSuccess ||
              (!songRequest.trim() && !storyText.trim())
            }
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
              ) : (
                <Send />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: 14, sm: 15 },
              borderRadius: 3,
              px: 3.5,
              py: 1.25,
              backgroundColor: submitSuccess ? "#10B981" : "#E50914",
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: submitSuccess ? "#059669" : "#C1121F",
              },
              "&:disabled": {
                backgroundColor: "rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.3)",
              },
            }}
          >
            {isSubmitting ? "Sending..." : submitSuccess ? "Sent!" : "Send Request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Modal for Song Request / Story Text */}
      {pendingRequest && (
        <PaymentForKaraokeRequest
          open={showPaymentModal}
          onClose={handleClosePaymentModal}
          flashCardId={Number(flashCardId)}
          requestType={pendingRequest.type}
          requestText={pendingRequest.text}
          requestPrice={cardData?.price || 10} // Use card price or default to 10 chips
          purchaseChip={purchasedChips}
          earnedChip={earnedChips}
          onConfirm={handlePaymentConfirm}
        />
      )}
    </>
  );
}
